import { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import {
  IonApp,
  IonRouterOutlet,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  IonSplitPane,
  IonMenu,
  IonContent,
  IonList,
  IonItem,
  IonMenuToggle,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonSpinner,
} from '@ionic/react';
import {
  homeOutline,
  createOutline,
  listOutline,
  rocketOutline,
  statsChartOutline,
  linkOutline,
  calendarOutline,
  settingsOutline,
  shieldCheckmarkOutline,
  logOutOutline,
  sparklesOutline,
  cardOutline,
  documentTextOutline,
  peopleOutline,
  eyeOutline,
  cashOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore, type SessionUser } from '@/store/auth.store';
import ToastHost from '@/components/ToastHost';
import LoginPage from '@/pages/LoginPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsPage from '@/pages/TermsPage';
import DashboardPage from '@/pages/DashboardPage';
import ComposerPage from '@/pages/ComposerPage';
import PostsPage from '@/pages/PostsPage';
import CalendarPage from '@/pages/CalendarPage';
import CampaignsPage from '@/pages/CampaignsPage';
import CampaignDetailPage from '@/pages/CampaignDetailPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AssistantPage from '@/pages/AssistantPage';
import AccountsPage from '@/pages/AccountsPage';
import SettingsPage from '@/pages/SettingsPage';
import BillingPage from '@/pages/BillingPage';
import AdminOpsPage from '@/pages/AdminOpsPage';
import UsersPage from '@/pages/UsersPage';
import ModerationPage from '@/pages/ModerationPage';
import BillingAdminPage from '@/pages/BillingAdminPage';

const EXTRA_MENU: Array<{ path: string; icon: string; label: string }> = [
  { path: '/app/assistant', icon: sparklesOutline, label: 'nav.assistant' },
  { path: '/app/calendar', icon: calendarOutline, label: 'nav.content' },
  { path: '/app/accounts', icon: linkOutline, label: 'nav.accounts' },
  { path: '/app/billing', icon: cardOutline, label: 'nav.billing' },
  { path: '/app/settings', icon: settingsOutline, label: 'nav.settings' },
];

export default function App() {
  const ready = useAuthStore((s) => s.ready);
  const user = useAuthStore((s) => s.user);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (!ready) {
    return (
      <IonApp>
        <div className="empty-state" style={{ paddingTop: '40vh' }}>
          <IonSpinner />
        </div>
      </IonApp>
    );
  }

  return (
    <IonApp>
      {user ? <AuthenticatedShell user={user} /> : <PublicRoutes />}
      <ToastHost />
    </IonApp>
  );
}

function PublicRoutes() {
  return (
    <IonRouterOutlet>
      <Switch>
        {/* Legal documents are public: a privacy policy must be readable before
            an account exists, which is exactly when it matters most. */}
        <Route exact path="/legal/privacy" component={PrivacyPolicyPage} />
        <Route exact path="/legal/terms" component={TermsPage} />
        <Route path="/login" component={LoginPage} />
        <Redirect to="/login" />
      </Switch>
    </IonRouterOutlet>
  );
}


function AuthenticatedShell({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);

  return (
    <IonSplitPane contentId="main-content" when="lg">
      <IonMenu contentId="main-content" type="overlay">
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              <img src="/logo.png" alt="Social Media Autopilot" className="app-header-logo" />
              <span>Social Media Autopilot</span>
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonList inset>
            {EXTRA_MENU.map((item) => (
              <IonMenuToggle key={item.path} auto-hide={false}>
                <IonItem button detail={false} routerLink={item.path}>
                  <IonIcon slot="start" icon={item.icon} />
                  <IonLabel>{t(item.label)}</IonLabel>
                </IonItem>
              </IonMenuToggle>
            ))}
            {user.role === 'admin' && (
              <>
                <IonMenuToggle auto-hide={false}>
                  <IonItem button detail={false} routerLink="/app/admin">
                    <IonIcon slot="start" icon={shieldCheckmarkOutline} />
                    <IonLabel>{t('admin.ops')}</IonLabel>
                  </IonItem>
                </IonMenuToggle>
                <IonMenuToggle auto-hide={false}>
                  <IonItem button detail={false} routerLink="/app/admin/users">
                    <IonIcon slot="start" icon={peopleOutline} />
                    <IonLabel>{t('admin.users')}</IonLabel>
                  </IonItem>
                </IonMenuToggle>
                <IonMenuToggle auto-hide={false}>
                  <IonItem button detail={false} routerLink="/app/admin/moderation">
                    <IonIcon slot="start" icon={eyeOutline} />
                    <IonLabel>{t('admin.moderation')}</IonLabel>
                  </IonItem>
                </IonMenuToggle>
                <IonMenuToggle auto-hide={false}>
                  <IonItem button detail={false} routerLink="/app/admin/billing">
                    <IonIcon slot="start" icon={cashOutline} />
                    <IonLabel>{t('admin.billingAdmin')}</IonLabel>
                  </IonItem>
                </IonMenuToggle>
              </>
            )}
            <IonMenuToggle auto-hide={false}>
              <IonItem button detail={false} onClick={() => void logout()}>
                <IonIcon slot="start" icon={logOutOutline} />
                <IonLabel>Sign out</IonLabel>
              </IonItem>
            </IonMenuToggle>
          </IonList>
          {/* Legal documents live outside the authenticated shell so they stay
              readable to signed-out visitors too. */}
          <IonList inset lines="none">
            <IonMenuToggle auto-hide={false}>
              <IonItem button detail={false} routerLink="/legal/privacy">
                <IonIcon slot="start" icon={shieldCheckmarkOutline} />
                <IonLabel>{t('legal.privacy')}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonMenuToggle auto-hide={false}>
              <IonItem button detail={false} routerLink="/legal/terms">
                <IonIcon slot="start" icon={documentTextOutline} />
                <IonLabel>{t('legal.terms')}</IonLabel>
              </IonItem>
            </IonMenuToggle>
          </IonList>
        </IonContent>
      </IonMenu>

      <IonRouterOutlet id="main-content">
        <Switch>
          <Route path="/app" component={TabLayout} />
          <Redirect to="/app" />
        </Switch>
      </IonRouterOutlet>
    </IonSplitPane>
  );
}

function TabLayout() {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <IonTabs>
      <IonRouterOutlet>
        <Switch>
          <Route exact path="/app" component={DashboardPage} />
          <Route exact path="/app/composer" component={ComposerPage} />
          <Route exact path="/app/posts" component={PostsPage} />
          <Route exact path="/app/calendar" component={CalendarPage} />
          <Route exact path="/app/campaigns" component={CampaignsPage} />
          <Route exact path="/app/campaigns/:id" component={CampaignDetailPage} />
          <Route exact path="/app/analytics" component={AnalyticsPage} />
          <Route exact path="/app/assistant" component={AssistantPage} />
          <Route exact path="/app/accounts" component={AccountsPage} />
          <Route exact path="/app/settings" component={SettingsPage} />
          <Route exact path="/app/billing" component={BillingPage} />
          <Route exact path="/app/admin" component={AdminOpsPage} />
          <Route exact path="/app/admin/users" component={UsersPage} />
          <Route exact path="/app/admin/moderation" component={ModerationPage} />
          <Route exact path="/app/admin/billing" component={BillingAdminPage} />
          <Redirect to="/app" />
        </Switch>
      </IonRouterOutlet>
      <IonTabBar slot="bottom">
        <IonTabButton tab="home" href="/app" selected={location.pathname === '/app'}>
          <IonIcon icon={homeOutline} />
          <IonLabel>{t('nav.home')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="composer" href="/app/composer" selected={location.pathname === '/app/composer'}>
          <IonIcon icon={createOutline} />
          <IonLabel>{t('content.newPost')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="posts" href="/app/posts" selected={location.pathname === '/app/posts'}>
          <IonIcon icon={listOutline} />
          <IonLabel>{t('nav.content')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="campaigns" href="/app/campaigns" selected={location.pathname.startsWith('/app/campaigns')}>
          <IonIcon icon={rocketOutline} />
          <IonLabel>{t('nav.campaigns')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="analytics" href="/app/analytics" selected={location.pathname === '/app/analytics'}>
          <IonIcon icon={statsChartOutline} />
          <IonLabel>{t('nav.analytics')}</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
}


